import { useMemo, useState } from 'react'
import type { AuthSession } from '../../../../types/plans'
import {
  buildMergedProposalPayload,
  buildPlanDiagnosticsClarifyMetadata,
  extractStagePreview,
  formatDiagnosticsItemLabel,
  isRedundantDiagnosticsNote,
  formatDiagnosticsPriceRub,
  formatMissingRequiredFields,
  MIS_PLAN_MATCHER_RECOMPOSE_HINT,
  PLAN_DIAGNOSTICS_COMPOSE_ACTION,
} from './planDiagnosticsCompose'
import { VoiceClarifyMicButton } from './VoiceClarifyMicButton'

type AiProposal = {
  proposal_id: string
  proposed_payload?: Record<string, unknown> | null
  override_payload?: Record<string, unknown> | null
  missing_required_fields?: string[]
  apply_error_text?: string | null
}

export function PlanDiagnosticsReviewPanel({
  apiBase,
  session,
  misPatientId,
  staffUserId,
  uiSurface,
  proposal,
  onProposalChange,
  onApplied,
  correctionMode = false,
  confirmLabel = 'Применить план в Планы МИС пациента',
}: {
  apiBase: string
  session: AuthSession
  misPatientId: string
  staffUserId: string
  uiSurface: string
  proposal: AiProposal
  onProposalChange: (proposal: AiProposal) => void
  onApplied: (data: unknown) => void
  correctionMode?: boolean
  confirmLabel?: string
}) {
  const authHeader = { Authorization: `Bearer ${session.accessToken}` }
  const [clarificationText, setClarificationText] = useState('')
  const [note, setNote] = useState('')
  const [overrideJson, setOverrideJson] = useState(() => JSON.stringify(buildMergedProposalPayload(proposal), null, 2))
  const [pending, setPending] = useState<'idle' | 'clarify' | 'override' | 'apply'>('idle')
  const [error, setError] = useState('')

  const previewStages = useMemo(
    () => extractStagePreview(buildMergedProposalPayload(proposal)),
    [proposal],
  )
  const missingFieldsText = formatMissingRequiredFields(proposal.missing_required_fields).join(', ')

  const previewTotals = useMemo(() => {
    let totalMinor = 0
    let pricedCount = 0
    for (const stage of previewStages) {
      for (const item of stage.items) {
        if (item.expectedCostMinor != null && item.expectedCostMinor > 0) {
          totalMinor += item.expectedCostMinor * (item.quantity ?? 1)
          pricedCount += 1
        }
      }
    }
    return { totalMinor, pricedCount }
  }, [previewStages])

  const clarify = async () => {
    const text = clarificationText.trim()
    if (!text) return
    setPending('clarify')
    setError('')
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action_key: PLAN_DIAGNOSTICS_COMPOSE_ACTION,
          ui_surface: uiSurface,
          source_kind: 'clarification_text',
          input_text: text,
          patient_id: misPatientId,
          metadata: buildPlanDiagnosticsClarifyMetadata({ proposal, staffUserId }),
        }),
      })
      if (!res.ok) throw new Error(`proposal HTTP ${res.status}`)
      const data = (await res.json()) as { proposal: AiProposal }
      onProposalChange(data.proposal)
      setOverrideJson(JSON.stringify(buildMergedProposalPayload(data.proposal), null, 2))
      setClarificationText('')
    } catch (e) {
      setError(String(e))
    } finally {
      setPending('idle')
    }
  }

  const saveOverride = async () => {
    setPending('override')
    setError('')
    try {
      const parsed = JSON.parse(overrideJson) as Record<string, unknown>
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals/${proposal.proposal_id}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ override_payload: parsed, note: note.trim() || undefined }),
      })
      if (!res.ok) throw new Error(`override HTTP ${res.status}`)
      const data = (await res.json()) as { proposal: AiProposal }
      onProposalChange(data.proposal)
    } catch (e) {
      setError(String(e))
    } finally {
      setPending('idle')
    }
  }

  const apply = async () => {
    setPending('apply')
    setError('')
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals/${proposal.proposal_id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ apply_immediately: true, note: note.trim() || undefined }),
      })
      if (!res.ok) {
        let detail = `confirm HTTP ${res.status}`
        try {
          const errBody = (await res.json()) as { detail?: unknown; error?: string }
          if (typeof errBody.detail === "string") detail = errBody.detail
          else if (errBody.detail) detail = JSON.stringify(errBody.detail)
          else if (errBody.error) detail = errBody.error
        } catch { /* ignore */ }
        throw new Error(detail)
      }
      const data = await res.json()
      onApplied(data)
    } catch (e) {
      setError(String(e))
    } finally {
      setPending('idle')
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
      <strong className="text-slate-900">Проверка и диалог с ИИ</strong>
      {previewTotals.pricedCount > 0 ? (
        <p className="text-xs text-slate-600">
          Оценка по прайсу: <span className="font-semibold text-slate-800">{formatDiagnosticsPriceRub(previewTotals.totalMinor, 1)}</span>
          {' '}({previewTotals.pricedCount} поз. с ценой)
        </p>
      ) : previewStages.length > 0 ? (
        <p className="text-xs text-amber-700">Цены из прайса подтянутся после пересборки черновика. Нажмите «Уточнить и пересобрать» с пустым текстом или заново «Составить план».</p>
      ) : null}
      {previewStages.length > 0 ? (
        <div className="grid gap-2">
          {previewStages.map((stage) => (
            <article key={stage.key} className="rounded-lg border border-white bg-white p-3 text-xs">
              <div className="font-semibold text-slate-800">
                {stage.stageType}
                {stage.clinicalObjective ? ` · ${stage.clinicalObjective}` : ''}
              </div>
              <div className="mt-2 grid gap-1">
                {stage.items.map((item) => (
                  <div key={item.key} className="rounded bg-slate-50 px-2 py-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="font-medium text-slate-800">{formatDiagnosticsItemLabel(item)}</div>
                      {formatDiagnosticsPriceRub(item.expectedCostMinor, item.quantity) ? (
                        <div className="shrink-0 font-medium text-emerald-700">
                          {formatDiagnosticsPriceRub(item.expectedCostMinor, item.quantity)}
                        </div>
                      ) : (
                        <div className="shrink-0 text-[10px] text-amber-600">цена не в прайсе</div>
                      )}
                    </div>
                    <div className="text-slate-500">
                      Зубы: {item.teeth.length > 0 ? item.teeth.join(', ') : 'Полость рта'}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          AI не вернул позиции с зубами и услугами. Уточните запрос или откройте JSON ниже.
        </p>
      )}

      {missingFieldsText ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Не хватает: {missingFieldsText}
        </p>
      ) : null}

      {proposal.apply_error_text ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{proposal.apply_error_text}</p>
      ) : null}

      {correctionMode ? (
        <p className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-900">
          Корректировка уже применённого ИИ-плана. «Применить» создаст <strong>новый</strong> план в МИС.
        </p>
      ) : null}

      <label className="grid gap-1 text-xs">
        <span className="flex flex-wrap items-center gap-2 font-medium text-slate-700">
          Уточнение для ИИ
          <VoiceClarifyMicButton
            apiBase={apiBase}
            authHeader={authHeader}
            uiSurface={uiSurface}
            actionKey={PLAN_DIAGNOSTICS_COMPOSE_ACTION}
            misPatientId={misPatientId}
            disabled={pending !== 'idle'}
            onTranscript={(text) => setClarificationText((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))}
          />
        </span>
        <textarea
          className="rounded border border-slate-200 p-2"
          rows={3}
          value={clarificationText}
          onChange={(e) => setClarificationText(e.target.value)}
          placeholder="Что изменить в плане…"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        {correctionMode ? (
          <button
            type="button"
            className="rounded-md border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs text-violet-900 disabled:opacity-50"
            disabled={pending !== 'idle'}
            onClick={() => setClarificationText(MIS_PLAN_MATCHER_RECOMPOSE_HINT)}
          >
            Подставить запрос matcher
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs disabled:opacity-50"
          disabled={pending !== 'idle' || !clarificationText.trim()}
          onClick={() => void clarify()}
        >
          {pending === 'clarify' ? 'Пересобираем…' : 'Уточнить и пересобрать'}
        </button>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer font-medium text-slate-700">Ручная правка (JSON)</summary>
        <textarea className="mt-2 w-full rounded border border-slate-200 p-2 font-mono text-[10px]" rows={6} value={overrideJson} onChange={(e) => setOverrideJson(e.target.value)} />
      </details>
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-600">Подробные данные (JSON)</summary>
        <pre className="mt-2 max-h-48 overflow-auto rounded bg-white p-2 text-[10px]">
          {JSON.stringify(buildMergedProposalPayload(proposal), null, 2)}
        </pre>
      </details>
      <label className="grid gap-1 text-xs">
        <span className="font-medium text-slate-700">Комментарий к применению</span>
        <textarea className="rounded border border-slate-200 p-2" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-md border border-slate-300 px-3 py-1.5 text-xs disabled:opacity-50" disabled={pending !== 'idle'} onClick={() => void saveOverride()}>
          {pending === 'override' ? 'Сохраняем…' : 'Сохранить правки'}
        </button>
        <button type="button" className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs text-white disabled:opacity-50" disabled={pending !== 'idle' || Boolean(missingFieldsText)} onClick={() => void apply()} title="Создаёт план в MIS с меткой ИИ">
          {pending === 'apply' ? 'Применяем…' : confirmLabel}
        </button>
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  )
}
